import {
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	HostBinding,
	Input,
	OnDestroy,
	OnInit,
	ViewEncapsulation,
} from "@angular/core";
import {
	AccountAppValue,
	AccountAppCommand,
	OmsMessage,
	OmsMessageAction,
	OmsMessageStatus,
} from "@fabric-types/app-account";
import { Store } from "@ngrx/store";
import { ExpandableTextOptions } from "@obg/mat.ui.common/expandable-text";
import { CoreState, LoadingService } from "@obg/ngx.core";
import { DocumentRef, TelemetryService, WindowRef } from "@obg/ngx.global.evo";
import { RouteSelector } from "@obg/ngx.router";
import { ViewportBreakpointService } from "@obg/ngx.ui.global";
import { ignoreNil } from "@obg/rxjs";
import { merge, Observable, Subscription } from "rxjs";
import { filter, switchMap, take, tap } from "rxjs/operators";

import { OMSEvent } from "../telemetry/ui-interaction-telemetry.model";

@Component({
	templateUrl: "./sm-oms.container.html",
	selector: "obg-m-sm-oms-container",
	styleUrls: ["./sm-oms.container.scss"],
	changeDetection: ChangeDetectionStrategy.OnPush,
	encapsulation: ViewEncapsulation.None,
})
export class SmOmsContainer implements OnInit, OnDestroy {
	@HostBinding("class.obg-m-sm-oms") hostCssClass = true;

	@Input() status: OmsMessageStatus | undefined;

	activeMessageStatus = OmsMessageStatus.Active;
	isLoading = true;
	messages: OmsMessage[] = [];
	expandableTextOptions: ExpandableTextOptions | undefined;

	private data$$: Subscription | undefined;

	constructor(
		private store: Store<CoreState>,
		private changeDetector: ChangeDetectorRef,
		private viewportService: ViewportBreakpointService,
		private loadingService: LoadingService,
		private windowRef: WindowRef,
		private telemetryService: TelemetryService,
		private documentRef: DocumentRef
	) {}

	ngOnInit() {
		const messages$ = this.store.select(RouteSelector.getCurrent).pipe(
			ignoreNil(),
			switchMap((route) => {
				const messageId = route.queryParams?.id;
				this.status = route.data.status;
				return this.loadMessages$(this.status as OmsMessageStatus, messageId);
			})
		);

		const viewport$ = this.viewportService.viewportSize$.pipe(
			tap(() => {
				this.expandableTextOptions = {
					maxCharacters: 110,
				};
			})
		);

		const isBusy$ = new Observable(this.documentRef.obgEventBus.retrieve(AccountAppValue.GetOmsIsBusy)).pipe(
			tap((isBusy) => (this.isLoading = isBusy))
		);

		this.data$$ = merge(isBusy$, messages$, viewport$).subscribe(() => this.changeDetector.markForCheck());
	}

	ngOnDestroy() {
		this.data$$?.unsubscribe();
	}

	loadMessages$(status: OmsMessageStatus, messageId: number | undefined = undefined) {
		return new Observable(
			this.documentRef.obgEventBus.retrieve(AccountAppValue.GetNotificationsByStatus, {
				status,
			})
		).pipe(
			ignoreNil(),
			tap(
				(messages) =>
					(this.messages = messages.filter((message) =>
						messageId ? message.id === Number(messageId) && !message.isHidden : !message.isHidden
					))
			)
		);
	}

	onDeleteMessageClick(messageId: number) {
		this.documentRef.obgEventBus?.dispatch(AccountAppCommand.UpdateMessage, {
			update: {
				messageId,
				action: OmsMessageAction.Hide,
			},
			onSuccess: () => this.refreshData(),
		});
	}

	onMessageCtaClick(message: OmsMessage) {
		if (!message.id || !message.content) {
			return;
		}

		this.documentRef.obgEventBus?.dispatch(AccountAppCommand.UpdateMessages, {
			updates: [
				{
					messageId: message.id,
					action: OmsMessageAction.Read,
				},
				{
					messageId: message.id,
					action: OmsMessageAction.Clicked,
				},
			],
			onSuccess: () => this.refreshData(),
		});

		this.telemetryService.track({
			event: OMSEvent.OMSClick,
			Action: message.content.cta.text,
			OMStitle: message.content.title,
		});

		this.loadingService.open();

		new Observable(this.documentRef.obgEventBus.retrieve(AccountAppValue.GetOmsIsBusy))
			.pipe(
				filter((isBusy) => !isBusy),
				take(1)
			)
			// eslint-disable-next-line rxjs/no-ignored-subscription
			.subscribe(() =>
				message.content && message.content.cta.url
					? (this.windowRef.native.location.href = this.getMessageHref(message.content.cta.url))
					: undefined
			);
	}

	trackMessage(_index: number, message: OmsMessage): number {
		return message.id!;
	}

	private refreshData() {
		this.documentRef.obgEventBus?.dispatch(AccountAppCommand.RefetchPollNotificationsInfo);
		this.loadMessages$(this.status as OmsMessageStatus)
			.pipe(take(1))
			// eslint-disable-next-line rxjs/no-ignored-subscription
			.subscribe();
	}

	private getMessageHref(url: string): string {
		return /^[a-z]+:/i.test(url)
			? url
			: `${this.windowRef.native.location.protocol}//${this.windowRef.native.location.host}${this.prependSlash(
					url
				)}`;
	}

	private prependSlash(url: string): string {
		return url.charAt(0) !== "/" ? `/${url}` : url;
	}
}
